import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  TripsRawEventName,
  useEmitCoreTripsRawEventOnce,
} from "@b2bportal/core-trips";
import { CoreTripsComponent, type TripProduct } from "@b2bportal/core-types";
import { Loading } from "@b2bportal/core-ui";
import type { Product } from "@b2bportal/purchase-api";
import type { MyTripsFilter } from "@hopper-b2b/types";
import { useTripsList, type TripsListProps } from "@tenant/trips";
import styles from "./styles.module.scss";
import { NoTripResults, TripTabsFilter } from "@/components";

const COMPONENT_KEY = CoreTripsComponent.TripsList;

type BaseProductSet = Partial<{ [k in Product]: TripProduct<unknown> }>;

export const TripsList = <Products extends BaseProductSet>(
  props: TripsListProps<Products>
) => {
  const tripFilterKey = "selectedTab";
  const [searchParams, setSearchParams] = useSearchParams();
  const [tripsFilter, setTripsFilter] = useState(
    searchParams.get(tripFilterKey) as MyTripsFilter
  );

  const { context } = useTripsList(props);

  useEmitCoreTripsRawEventOnce(
    {
      name: TripsRawEventName.ViewedMyTrips,
    },
    !context.isLoading
  );

  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const tabClickHandler = useCallback(
    (selectedTab: MyTripsFilter) => {
      setTripsFilter(selectedTab);
      setSearchParams({ selectedTab });
    },
    [setSearchParams]
  );

  return (
    <div className={block}>
      {context.isLoading ? (
        <Loading className={cn("loader")} />
      ) : (
        <>
          <TripTabsFilter
            onClick={tabClickHandler}
            selectedFilter={tripsFilter}
          />
          {context.trips.length > 0 ? (
            <ul className={cn("trips-list")}>
              {context.trips.map((wrapper) => (
                <li className={cn("trip")} key={wrapper.reservationId}>
                  <wrapper.component />
                </li>
              ))}
            </ul>
          ) : (
            <NoTripResults category={tripsFilter} />
          )}
        </>
      )}
    </div>
  );
};
